import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import { DISCLAIMER_TEXT } from "../lib/constants";
import { Music4, Video, ChevronLeft, Loader2, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

function ChoiceCard({ kind, title, accent, icon: Icon, onClick, testId, disabled, disabledLabel }) {
  return (
    <div
      className={`relative bg-[#0E0E11] border border-white/10 rounded-2xl p-7 md:p-8 flex flex-col transition-all hover:-translate-y-1 ${disabled ? "opacity-60" : ""}`}
      style={{
        boxShadow: `0 0 0 1px ${accent}33, 0 24px 48px -20px ${accent}55`,
        borderTop: `4px solid ${accent}`,
      }}
    >
      <div
        className="absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl opacity-20"
        style={{ background: accent }}
      />
      <div className="flex items-center gap-3 mb-4 relative">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: `${accent}1A`, color: accent, border: `1px solid ${accent}33` }}
        >
          <Icon className="w-6 h-6" />
        </div>
        <h2 className="text-2xl font-bold">{title}</h2>
      </div>
      <p className="text-white/70 text-sm whitespace-pre-wrap leading-relaxed mb-6 relative">
        {DISCLAIMER_TEXT}
      </p>
      <Button
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
        className="mt-auto h-12 rounded-full font-bold tracking-widest text-white border-0 transition-all hover:-translate-y-0.5 disabled:opacity-50"
        style={{
          background: disabled
            ? "linear-gradient(135deg, #555 0%, #333 100%)"
            : `linear-gradient(135deg, ${accent} 0%, #00E5FF 100%)`,
          boxShadow: disabled ? "none" : `0 0 25px ${accent}66`,
        }}
      >
        {disabled ? (
          <span className="inline-flex items-center gap-2">
            <Lock className="w-4 h-4" /> {disabledLabel || "CHIUSO"}
          </span>
        ) : (
          "PROCEDI"
        )}
      </Button>
    </div>
  );
}

// (RequestCompetitionCard moved to Landing page)

export default function CompetitionChoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/competitions/${id}`)
      .then((r) => setComp(r.data))
      .catch(() => toast.error("Competizione non trovata"))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          className="text-white/60 hover:text-white text-sm flex items-center gap-1 mb-6"
        >
          <ChevronLeft className="w-4 h-4" /> Indietro
        </button>

        {loading && (
          <div className="flex items-center gap-2 text-white/60">
            <Loader2 className="w-5 h-5 animate-spin" /> Caricamento...
          </div>
        )}

        {comp && (
          <>
            <div className="mb-10">
              <div className="text-xs uppercase tracking-[0.3em] text-white/40">
                Competizione
              </div>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight mt-2">
                {comp.name}
              </h1>
              <div className="text-white/60 text-sm mt-2">
                {comp.location} • {comp.start_date}
                {comp.end_date !== comp.start_date && ` → ${comp.end_date}`}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <ChoiceCard
                kind="musica"
                title="INVIO Musica"
                accent="#FF5722"
                icon={Music4}
                onClick={() => navigate(`/competizioni/${id}/musica`)}
                testId="choice-music-btn"
                disabled={comp?.closed}
                disabledLabel="CHIUSA"
              />
              <ChoiceCard
                kind="video"
                title="INVIO Video"
                accent="#00E5FF"
                icon={Video}
                onClick={() => navigate(`/competizioni/${id}/video`)}
                testId="choice-video-btn"
                disabled={comp?.closed}
                disabledLabel="CHIUSA"
              />
            </div>
            {comp?.closed && (
              <div
                data-testid="competition-closed-banner"
                className="max-w-3xl mx-auto mt-6 bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-2xl p-4 text-center text-sm"
              >
                Questa competizione è chiusa: gli invii non sono più accettati.
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
